import { useCallback, useEffect, useRef, useState } from "react";
import type { MutableRefObject } from "react";
import type { Chart as KChart } from "klinecharts";


interface UseViewportManagerOptions {
  chartRef: MutableRefObject<KChart | null>;
  enabled: boolean;
  isReplay: boolean;
  livePrice: number | null;
  symbol: string;
}

const LIVE_EDGE_PADDING_BARS = 3;

export function forceChartJumpToLive() {
  if (typeof window === "undefined") {
    return;
  }

  const jumpToLive = (
    window as typeof window & {
      __LYTICK_JUMP_TO_LIVE__?: () => void;
    }
  ).__LYTICK_JUMP_TO_LIVE__;

  if (jumpToLive) {
    jumpToLive();
  }
}

export function useViewportManager({
  chartRef,
  enabled,
  isReplay,
  livePrice,
  symbol,
}: UseViewportManagerOptions) {
  const [isUserScrolled, setIsUserScrolled] = useState(false);
  const suppressViewportLockRef = useRef(false);
  const suppressTimerRef = useRef<number | null>(null);

  const clearSuppression = useCallback(() => {
    if (suppressTimerRef.current !== null) {
      window.clearTimeout(suppressTimerRef.current);
      suppressTimerRef.current = null;
    }
    suppressViewportLockRef.current = false;
  }, []);

  const withSuppressedViewportLock = useCallback(
    (work: () => void) => {
      suppressViewportLockRef.current = true;
      if (suppressTimerRef.current !== null) {
        window.clearTimeout(suppressTimerRef.current);
      }

      try {
        work();
      } finally {
        suppressTimerRef.current = window.setTimeout(() => {
          suppressViewportLockRef.current = false;
          suppressTimerRef.current = null;
        }, 120);
      }
    },
    [],
  );

  const updateScrollIntent = useCallback(() => {
    const chart = chartRef.current;
    if (!chart || suppressViewportLockRef.current) {
      return;
    }

    try {
      const visibleRange = chart.getVisibleRange();
      const dataLength = chart.getDataList().length;
      const liveEdgeIndex = Math.max(0, dataLength - 1);
      const atLiveEdge = visibleRange.to >= liveEdgeIndex - LIVE_EDGE_PADDING_BARS;
      setIsUserScrolled(!atLiveEdge);
    } catch {
      // Ignore viewport reads on disposed charts.
    }
  }, [chartRef]);

  const jumpToLive = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }

    setIsUserScrolled(false);
    withSuppressedViewportLock(() => {
      chart.scrollToRealTime(80);
    });
  }, [chartRef, withSuppressedViewportLock]);

  useEffect(() => {
    setIsUserScrolled(false);
  }, [symbol]);

  // livePrice !== null signals the chart has booted and received data; use it as the
  // trigger because chartRef is a stable ref whose .current won't re-fire effects.
  const chartReady = livePrice !== null;
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }

    const handleScroll = () => {
      updateScrollIntent();
    };

    chart.subscribeAction("onScroll", handleScroll);
    chart.subscribeAction("onVisibleRangeChange", handleScroll);

    return () => {
      chart.unsubscribeAction("onScroll", handleScroll);
      chart.unsubscribeAction("onVisibleRangeChange", handleScroll);
    };
  }, [chartRef, updateScrollIntent, chartReady]);

  // Replay auto-scroll removed: user controls viewport manually.
  // The replay position line (overlay) shows where replay is at all times.

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }

    const win = window as typeof window & {
      __LYTICK_JUMP_TO_LIVE__?: () => void;
      __LYTICK_WITH_SUPPRESSED_VIEWPORT_LOCK__?: (fn: () => void) => void;
      __LYTICK_USER_SCROLLED__?: boolean;
    };

    win.__LYTICK_JUMP_TO_LIVE__ = jumpToLive;
    win.__LYTICK_WITH_SUPPRESSED_VIEWPORT_LOCK__ = withSuppressedViewportLock;
    win.__LYTICK_USER_SCROLLED__ = isUserScrolled;

    return () => {
      win.__LYTICK_JUMP_TO_LIVE__ = undefined;
      win.__LYTICK_WITH_SUPPRESSED_VIEWPORT_LOCK__ = undefined;
      win.__LYTICK_USER_SCROLLED__ = undefined;
      clearSuppression();
    };
  }, [clearSuppression, enabled, isUserScrolled, jumpToLive, withSuppressedViewportLock]);

  return {
    isUserScrolled,
    jumpToLive,
    withSuppressedViewportLock,
  };
}
